import { z } from "zod";
import { pageOf, PageQuerySchema } from "../common/pagination.js";
import { DateOnlySchema, IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { DisciplineSchema } from "../common/tunisia.js";
import { ProjectParamsSchema } from "../projects/index.js";
const TASK_STATUSES = ["todo", "in_progress", "blocked", "done"];
const TaskStatusSchema = z.enum(TASK_STATUSES);
const TASK_PRIORITIES = ["low", "normal", "high", "urgent"];
const TaskPrioritySchema = z.enum(TASK_PRIORITIES);
const COMMENT_SUBJECT_TYPES = ["task", "document", "deliverable", "annotation"];
const CommentSubjectTypeSchema = z.enum(COMMENT_SUBJECT_TYPES);
const RICH_TEXT_MAX_DEPTH = 10;
const RICH_TEXT_MAX_TEXT = 1e4;
const RICH_TEXT_MAX_BYTES = 5e4;
const RICH_TEXT_MAX_MENTIONS = 50;
function isWebUrl(value) {
  const lower = value.toLowerCase();
  const rest = lower.startsWith("https://") ? value.slice(8) : lower.startsWith("http://") ? value.slice(7) : null;
  if (!rest || ["/", "?", "#", "\\"].includes(rest.charAt(0))) return false;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 32 || code === 127) return false;
  }
  return true;
}
const LinkHrefSchema = z.string().max(2e3).refine(isWebUrl, "a link must be an http or https URL");
const MarkSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("bold") }),
  z.strictObject({ type: z.literal("italic") }),
  z.strictObject({ type: z.literal("code") }),
  z.strictObject({
    type: z.literal("link"),
    attrs: z.strictObject({
      href: LinkHrefSchema,
      target: z.enum(["_blank", "_self"]).nullish(),
      rel: z.string().max(100).nullish()
    })
  })
]);
const TextNodeSchema = z.strictObject({
  type: z.literal("text"),
  text: z.string().min(1).max(RICH_TEXT_MAX_TEXT),
  marks: z.array(MarkSchema).max(4).optional()
});
const HardBreakSchema = z.strictObject({ type: z.literal("hardBreak") });
const MentionSchema = z.strictObject({
  type: z.literal("mention"),
  attrs: z.strictObject({ id: IdSchema, label: z.string().max(200) })
});
const InlineNodeSchema = z.discriminatedUnion("type", [TextNodeSchema, HardBreakSchema, MentionSchema]).meta({ id: "RichTextInline" });
function listItemNode(depth) {
  return z.strictObject({
    type: z.literal("listItem"),
    ...depth < RICH_TEXT_MAX_DEPTH ? {
      content: z.array(blockNode(depth + 1)).max(100).optional()
    } : {}
  });
}
function blockNode(depth) {
  const room = depth < RICH_TEXT_MAX_DEPTH;
  const inline = room ? { content: z.array(InlineNodeSchema).max(500).optional() } : {};
  const items = room ? {
    content: z.array(listItemNode(depth + 1)).max(200).optional()
  } : {};
  return z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("paragraph"), ...inline }),
    z.strictObject({
      type: z.literal("heading"),
      attrs: z.strictObject({ level: z.union([z.literal(2), z.literal(3)]) }),
      ...inline
    }),
    z.strictObject({ type: z.literal("bulletList"), ...items }),
    z.strictObject({ type: z.literal("orderedList"), ...items })
  ]);
}
function byteLength(value) {
  const json = JSON.stringify(value);
  let bytes = 0;
  for (let i = 0; i < json.length; i++) {
    const code = json.charCodeAt(i);
    if (code < 128) bytes += 1;
    else if (code < 2048) bytes += 2;
    else if (code >= 55296 && code <= 56319) {
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}
function mentionedUserIds(doc) {
  const found = /* @__PURE__ */ new Set();
  const stack = [...doc.content].reverse();
  while (stack.length > 0 && found.size < RICH_TEXT_MAX_MENTIONS) {
    const node = stack.pop();
    if (node.type === "mention" && node.attrs?.id) found.add(node.attrs.id);
    if (node.content)
      for (let i = node.content.length - 1; i >= 0; i--)
        stack.push(node.content[i]);
  }
  return [...found];
}
function distinctMentions(doc) {
  const found = /* @__PURE__ */ new Set();
  const stack = [...doc.content];
  while (stack.length > 0) {
    const node = stack.pop();
    if (node.type === "mention" && node.attrs?.id) found.add(node.attrs.id);
    if (node.content) stack.push(...node.content);
  }
  return found.size;
}
const RichTextDocSchema = z.strictObject({
  type: z.literal("doc"),
  content: z.array(blockNode(2)).max(200)
}).superRefine((doc, ctx) => {
  if (byteLength(doc) > RICH_TEXT_MAX_BYTES)
    ctx.addIssue({
      code: "custom",
      message: `a document holds at most ${String(RICH_TEXT_MAX_BYTES)} bytes of JSON`
    });
  if (distinctMentions(doc) > RICH_TEXT_MAX_MENTIONS)
    ctx.addIssue({
      code: "custom",
      message: `a document mentions at most ${String(RICH_TEXT_MAX_MENTIONS)} people`
    });
}).meta({ id: "RichTextDoc" });
const TASK_PERMISSIONS = ["can_view", "can_contribute"];
const TaskPermissionSchema = z.enum(TASK_PERMISSIONS);
const TaskParamsSchema = z.strictObject({ orgId: IdSchema, taskId: IdSchema });
const ProjectTasksParamsSchema = ProjectParamsSchema;
const TaskDependencyParamsSchema = z.strictObject({
  orgId: IdSchema,
  taskId: IdSchema,
  dependsOnTaskId: IdSchema
});
const TaskChecklistItemParamsSchema = z.strictObject({
  orgId: IdSchema,
  taskId: IdSchema,
  itemId: IdSchema
});
const TaskCommentParamsSchema = z.strictObject({
  orgId: IdSchema,
  taskId: IdSchema,
  commentId: IdSchema
});
const TaskAssigneeSchema = z.strictObject({
  userId: IdSchema,
  displayName: z.string()
});
const TaskSchema = z.strictObject({
  id: IdSchema,
  projectId: IdSchema,
  phaseId: IdSchema.nullable(),
  parentTaskId: IdSchema.nullable(),
  title: z.string(),
  description: RichTextDocSchema.nullable(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  discipline: DisciplineSchema.nullable(),
  startsOn: DateOnlySchema.nullable(),
  dueOn: DateOnlySchema.nullable(),
  /** Position in the board column of its status (compare as plain strings, never as numbers). */
  boardRank: z.string(),
  assignees: z.array(TaskAssigneeSchema),
  checklist: z.strictObject({ done: z.int().min(0), total: z.int().min(0) }),
  /** Tasks that must be done before this one (finish-to-start). */
  dependsOn: z.array(IdSchema),
  subtaskCount: z.int().min(0),
  createdBy: IdSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1),
  /** `can_contribute` is present when the caller may change the task. */
  _permissions: z.array(TaskPermissionSchema)
});
const TaskChecklistItemSchema = z.strictObject({
  id: IdSchema,
  label: z.string(),
  done: z.boolean(),
  ordinal: z.int().min(0)
});
const TaskDetailSchema = TaskSchema.extend({
  checklistItems: z.array(TaskChecklistItemSchema),
  subtasks: z.array(TaskSchema)
});
const TaskListQuerySchema = PageQuerySchema.extend({
  assigneeId: IdSchema.optional(),
  discipline: DisciplineSchema.optional(),
  phaseId: IdSchema.optional(),
  status: TaskStatusSchema.optional(),
  dueBefore: DateOnlySchema.optional(),
  parentTaskId: z.union([IdSchema, z.literal("none")]).optional(),
  q: z.string().trim().min(1).max(100).optional()
});
const TaskListSchema = pageOf(TaskSchema);
const TaskTitleSchema = z.string().trim().min(1).max(200);
const AssigneeIdsSchema = z.array(IdSchema).max(20).refine((ids) => new Set(ids).size === ids.length, "assignees must be distinct");
const CreateTaskRequestSchema = z.strictObject({
  title: TaskTitleSchema,
  description: RichTextDocSchema.nullish(),
  phaseId: IdSchema.nullish(),
  /** A top-level task of the same project (one subtask level only). */
  parentTaskId: IdSchema.nullish(),
  status: TaskStatusSchema.default("todo"),
  priority: TaskPrioritySchema.default("normal"),
  discipline: DisciplineSchema.nullish(),
  /** Planned start (PLN-BE-01: the Gantt bar); not after the due date. */
  startsOn: DateOnlySchema.nullish(),
  dueOn: DateOnlySchema.nullish(),
  /** Members of the project. */
  assigneeIds: AssigneeIdsSchema.default([])
});
const UpdateTaskRequestSchema = z.strictObject({
  title: TaskTitleSchema,
  description: RichTextDocSchema.nullable(),
  phaseId: IdSchema.nullable(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  discipline: DisciplineSchema.nullable(),
  startsOn: DateOnlySchema.nullable(),
  dueOn: DateOnlySchema.nullable(),
  assigneeIds: AssigneeIdsSchema
}).partial().refine((body) => Object.keys(body).length > 0, "send at least one field to change");
const MoveTaskRequestSchema = z.strictObject({
  status: TaskStatusSchema,
  beforeTaskId: IdSchema.optional(),
  afterTaskId: IdSchema.optional()
});
const AddTaskDependencyRequestSchema = z.strictObject({ dependsOnTaskId: IdSchema });
const TaskDependencyListSchema = z.strictObject({ dependsOn: z.array(IdSchema) });
const ChecklistLabelSchema = z.string().trim().min(1).max(200);
const CreateChecklistItemRequestSchema = z.strictObject({ label: ChecklistLabelSchema });
const UpdateChecklistItemRequestSchema = z.strictObject({ label: ChecklistLabelSchema, done: z.boolean() }).partial().refine((body) => Object.keys(body).length > 0, "send at least one field to change");
const CommentSchema = z.strictObject({
  id: IdSchema,
  subjectType: CommentSubjectTypeSchema,
  subjectId: IdSchema,
  author: z.strictObject({ id: IdSchema, displayName: z.string() }),
  body: RichTextDocSchema,
  /** Active members of the organisation mentioned in the body. */
  mentions: z.array(IdSchema),
  editedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1)
});
const CommentListSchema = pageOf(CommentSchema);
const CommentListQuerySchema = PageQuerySchema;
const CreateCommentRequestSchema = z.strictObject({ body: RichTextDocSchema });
const UpdateCommentRequestSchema = z.strictObject({ body: RichTextDocSchema });
export {
  AddTaskDependencyRequestSchema,
  COMMENT_SUBJECT_TYPES,
  CommentListQuerySchema,
  CommentListSchema,
  CommentSchema,
  CommentSubjectTypeSchema,
  CreateChecklistItemRequestSchema,
  CreateCommentRequestSchema,
  CreateTaskRequestSchema,
  MoveTaskRequestSchema,
  ProjectTasksParamsSchema,
  RICH_TEXT_MAX_BYTES,
  RICH_TEXT_MAX_DEPTH,
  RICH_TEXT_MAX_MENTIONS,
  RICH_TEXT_MAX_TEXT,
  RichTextDocSchema,
  TASK_PERMISSIONS,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TaskAssigneeSchema,
  TaskChecklistItemParamsSchema,
  TaskChecklistItemSchema,
  TaskCommentParamsSchema,
  TaskDependencyListSchema,
  TaskDependencyParamsSchema,
  TaskDetailSchema,
  TaskListQuerySchema,
  TaskListSchema,
  TaskParamsSchema,
  TaskPermissionSchema,
  TaskPrioritySchema,
  TaskSchema,
  TaskStatusSchema,
  UpdateChecklistItemRequestSchema,
  UpdateCommentRequestSchema,
  UpdateTaskRequestSchema,
  isWebUrl,
  mentionedUserIds
};
//# sourceMappingURL=index.js.map